// Account Performance section — FE-036 / spec §7.0.7 + Dashboard-Filters-01.
//
// Widget-level filter: risk-level chips (low / medium / high / critical).
// Applied client-side over the returned rows (BE-029 doesn't accept a
// risk_bucket filter on this endpoint).

import { useMemo, useState } from "react";

import {
  DashboardCard,
  DashboardEmptyState,
  DashboardErrorState,
  DashboardLoadingState,
  useClientPerformanceQuery,
  useCommonFilters,
} from "@/features/dashboard";
import {
  RiskLevelChips,
  type RiskLevel,
} from "@/features/dashboard/accountRisk";

import {
  AccountPerformanceTable,
  type AccountPerformanceSortKey,
} from "./AccountPerformanceTable";


export const ACCOUNT_PERFORMANCE_SECTION = "client-performance" as const;
export const ACCOUNT_PERFORMANCE_SPAN_CLASS = "xl:col-span-2 md:col-span-2";


const DEFAULT_LIMIT = 10;
const DEFAULT_SORT_BY: AccountPerformanceSortKey = "overdue_count";
const DEFAULT_SORT_DIR: "asc" | "desc" = "desc";


export function AccountPerformanceSection() {
  const filters = useCommonFilters();
  const [sortBy, setSortBy] =
    useState<AccountPerformanceSortKey>(DEFAULT_SORT_BY);
  const [sortDir, setSortDir] =
    useState<"asc" | "desc">(DEFAULT_SORT_DIR);
  const [offset, setOffset] = useState(0);
  const [activeLevels, setActiveLevels] = useState<RiskLevel[]>([]);

  const query = useClientPerformanceQuery({
    ...filters,
    sortBy, sortDir,
    limit: DEFAULT_LIMIT, offset,
  });

  const filteredRows = useMemo(() => {
    const rows = query.data?.rows ?? [];
    if (activeLevels.length === 0) return rows;
    const set = new Set(activeLevels);
    return rows.filter((r) => set.has(r.risk_bucket));
  }, [query.data, activeLevels]);

  function toggleLevel(l: RiskLevel): void {
    setActiveLevels((prev) =>
      prev.includes(l) ? prev.filter((x) => x !== l) : [...prev, l],
    );
  }

  return (
    <DashboardCard
      section={ACCOUNT_PERFORMANCE_SECTION}
      className={ACCOUNT_PERFORMANCE_SPAN_CLASS}
      title="Account performance"
      subtitle="Open, overdue, and completed Commitments by Account."
      actions={
        <RiskLevelChips
          value={activeLevels}
          onToggle={toggleLevel}
          testId="account-performance-risk-chips"
        />
      }
    >
      {query.isLoading ? (
        <DashboardLoadingState variant="table" rows={5} />
      ) : query.isError ? (
        <DashboardErrorState
          title="Couldn't load Account performance"
          retry={() => query.refetch()}
        />
      ) : query.data && filteredRows.length > 0 ? (
        <AccountPerformanceTable
          rows={filteredRows}
          total={
            activeLevels.length === 0 ? query.data.total : filteredRows.length
          }
          sortBy={sortBy}
          sortDir={sortDir}
          onSortChange={({ sortBy: nextBy, sortDir: nextDir }) => {
            setSortBy(nextBy);
            setSortDir(nextDir);
            setOffset(0);
          }}
          limit={DEFAULT_LIMIT}
          offset={offset}
          onOffsetChange={setOffset}
        />
      ) : (
        <DashboardEmptyState
          title="No Account performance data yet"
          description="Once Accounts have Commitments, per-Account metrics appear here."
        />
      )}
    </DashboardCard>
  );
}
